import React, { useEffect, useState, useRef } from "react";
import { Camera, Award, Users, Star } from "lucide-react";

const About = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  // الأرقام الثابتة
  const counters = {
    projects: 500,
    clients: 300,
    experience: 5,
  };

  // استخدام Intersection Observer للكشف عن ظهور القسم
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            setVisible(true);
            hasAnimated.current = true;
          }
        });
      },
      { threshold: 0.2 } // يبدأ عند ظهور 20% من القسم
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const stats = [
    {
      icon: Camera,
      value: counters.projects,
      label: "مشروع مكتمل",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Users,
      value: counters.clients,
      label: "عميل سعيد",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: Award,
      value: counters.experience,
      label: "سنوات خبرة",
      color: "from-orange-500 to-red-500",
    },
  ];

  const skills = [
    { name: "تصوير البورتريه", level: 95 },
    { name: "تصوير الأفراح", level: 90 },
    { name: "تصوير المناسبات", level: 92 },
    { name: "تصوير الأزياء", level: 88 },
  ];

  return (
    <section 
      ref={sectionRef}
      className="relative bg-gradient-to-b from-gray-50 via-white to-gray-50 py-20 overflow-hidden" 
      id="about"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div
            className={`inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-full mb-4 transition-all duration-1000 ${
              visible ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          >
            <Star className="w-4 h-4" />
            <span className="font-medium">تعرف عليّ</span>
          </div>
          <h2
            className={`text-5xl font-bold text-gray-900 mb-4 transition-all duration-1000 delay-100 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            عنّي
          </h2>
          <div
            className={`w-24 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full transition-all duration-1000 delay-200 ${
              visible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
            }`}
          />
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-16 mb-20">
          {/* Image Section */}
          <div className="relative group">
            <div
              className={`absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-all duration-700 ${
                visible ? "scale-100" : "scale-0"
              }`}
            />
            <div
              className={`relative transition-all duration-1000 ${
                visible ? "opacity-100 translate-x-0 rotate-0" : "opacity-0 -translate-x-20 -rotate-6"
              }`}
            >
              <img
                src="/kemo.jpg"
                alt="Photographer portrait"
                className="w-80 h-96 lg:w-96 lg:h-[500px] object-cover rounded-3xl shadow-2xl border-4 border-white transform group-hover:scale-105 group-hover:rotate-2 transition-all duration-700"
              />
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 transform group-hover:scale-110 transition-all duration-500">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">محترف معتمد</p>
                    <p className="font-bold text-gray-900">مصور فوتوغرافي</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text Section */}
          <div className="flex-1 max-w-2xl" dir="rtl">
            <div
              className={`transition-all duration-1000 delay-300 ${
                visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"
              }`}
            >
              <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                مصور فوتوغرافي
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  محترف
                </span>
              </h3>
              <p className="text-gray-600 text-lg lg:text-xl leading-relaxed mb-8">
                لدي خبرة واسعة في تصوير البورتريه، حفلات الزفاف، المناسبات، والأزياء.
                أستخدم أحدث المعدات والأساليب الفنية للحصول على لقطات مميزة
                تعبّر عن شخصيتك أو حدثك بأجمل صورة ممكنة.
              </p>

              {/* Skills */}
              <div className="space-y-4 mb-8">
                {skills.map((skill, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-700 font-medium">{skill.name}</span>
                      <span className="text-gray-500">{skill.level}%</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                        style={{ width: `${skill.level}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500`} />
              
              <div className="relative text-center">
                <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-4xl font-bold text-gray-900 mb-2">
                  {stat.value}+
                </h4>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>

              {/* Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 group-hover:translate-x-full transition-all duration-1000 rounded-3xl" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;